/**
 * Closing documents — the pre-close upload section (closing-readiness.md §3b).
 *
 * One row per requirement from `GET /closing/readiness/{dealId}/closing-docs`
 * (unexecuted package, preliminary HUD, wire instructions, CPL, closing
 * instructions; acquisitions get the executed package + final HUD). The files
 * live on the loan like any other document — this is a focused view + uploader
 * over the same store, not a new one. Upload posts to the existing
 * `POST /files/upload_loan_docs/{dealId}` with the row's first docType (the
 * others are offered in a picker).
 *
 * "Compare to loan terms" runs the deal-vs-Note-vs-HUD tie-out
 * (`POST …/closing-docs/compare`). `termsTieOut.agrees`: true = green,
 * false = red with findings, null = "no extracts to compare yet" — never green.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import CompareArrowsRounded from "@mui/icons-material/CompareArrowsRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import RadioButtonUncheckedRounded from "@mui/icons-material/RadioButtonUncheckedRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, Button, CircularProgress, MenuItem, TextField, Tooltip } from "@mui/material";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import { closingReadinessApi } from "../../api/closingReadinessApi";
import { losAiReviewsApi } from "../../api/losAiReviewsApi";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";
import { filePreviewSetData } from "../../redux/reducers/filePreviewReducer";
import { amber, slate, status, steel } from "../../theme/colors";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { BlockTitle, Muted, Pill } from "./ReadinessBits";
import { docStatusMeta, isTrade } from "./closingReadinessUtils";

// Multipart filename must stay unique across re-uploads (useAxiosHook passes
// `id` as the filename the backend persists).
const stamp = () => new Date().toISOString().replace(/[:.]/g, "-");

const TieOutBanner = ({ tieOut, onCompare, comparing }) => {
  const agrees = tieOut?.agrees;
  const tone =
    agrees === true
      ? { c: status.success.fg, bg: status.success.bg, icon: CheckCircleOutlineRounded, text: "Docs tie to terms" }
      : agrees === false
        ? { c: status.error.fg, bg: status.error.bg, icon: WarningAmberOutlined, text: `Docs do not tie to terms (${tieOut?.findingCount ?? tieOut?.findings?.length ?? 0})` }
        : { c: slate[600], bg: slate[100], icon: RadioButtonUncheckedRounded, text: "No extracts to compare yet" };
  const Icon = tone.icon;
  return (
    <Box sx={{ borderRadius: "10px", background: tone.bg, px: 1.5, py: 1.25 }}>
      <Flx ac jb wrap g={1}>
        <Flx ac g={0.75}>
          <Icon sx={{ fontSize: 17, color: tone.c }} />
          <Txt sx={{ fontSize: 13, fontWeight: 700, color: tone.c }}>{tone.text}</Txt>
        </Flx>
        <Button
          size="small"
          variant="outlined"
          color="gray"
          startIcon={comparing ? <CircularProgress size={12} /> : <CompareArrowsRounded />}
          onClick={onCompare}
          disabled={comparing}
          sx={{ textTransform: "none", borderRadius: "8px", background: "#fff" }}
        >
          Compare to loan terms
        </Button>
      </Flx>
      {agrees === false && Array.isArray(tieOut?.findings) && tieOut.findings.length > 0 && (
        <Flx column g={0.35} sx={{ mt: 0.75 }}>
          {tieOut.findings.map((f, i) => (
            <Txt key={i} sx={{ fontSize: 12.5, color: slate[800] }}>
              • {f}
            </Txt>
          ))}
        </Flx>
      )}
      {agrees === false && (
        <Muted sx={{ display: "block", mt: 0.5 }}>
          Staged as findings in the AI Conditions queue. A false tie-out is a funding-gating item.
        </Muted>
      )}
    </Box>
  );
};

const RequirementRow = ({ item, dealId, docGroupFor, onUploaded, resolveDoc, onPreview }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { uploadLoanDocs } = useLoanFilesHook();
  const inputRef = useRef(null);
  const [docType, setDocType] = useState(item?.docTypes?.[0] || "");
  const [uploading, setUploading] = useState(false);
  useEffect(() => setDocType(item?.docTypes?.[0] || ""), [item?.key, item?.docTypes]);

  const onPick = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length || !dealId) return;
    const docGroup = docGroupFor(docType);
    const files = list.map((file) => ({
      file,
      id: `${file.name.replace(/\.[^.]+$/, "")}-${stamp()}${file.name.match(/\.[^.]+$/)?.[0] || ""}`,
      name: file.name,
      docGroup,
      docType,
    }));
    setUploading(true);
    await uploadLoanDocs({
      loanId: dealId,
      files,
      onSuccessMsg: `${item.label}: ${list.length} file${list.length === 1 ? "" : "s"} uploaded`,
      onSuccessFn: () => {
        setUploading(false);
        if (typeof onUploaded === "function") onUploaded();
      },
      onFailFn: () => {
        setUploading(false);
        enqueueSnackbar("Upload failed.", { variant: "error" });
      },
    });
  };

  const present = !!item?.present;
  const docs = Array.isArray(item?.docs) ? item.docs : [];
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: "10px",
        border: `1px solid ${present ? slate[200] : item?.required ? status.error.bg : slate[100]}`,
        background: present ? "#fff" : item?.required ? "#fffafa" : "#fff",
      }}
    >
      <Flx jb g={1.5} sx={{ alignItems: "flex-start" }}>
        <Flx g={1} sx={{ alignItems: "flex-start", minWidth: 0, flex: 1 }}>
          {present ? (
            <CheckCircleOutlineRounded sx={{ fontSize: 18, color: status.success.solid, mt: "1px" }} />
          ) : (
            <RadioButtonUncheckedRounded
              sx={{ fontSize: 18, color: item?.required ? status.error.solid : slate[300], mt: "1px" }}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Flx ac wrap g={0.75}>
              <Txt sx={{ fontSize: 13.5, fontWeight: 700, color: slate[900] }}>
                {item?.label}
                {item?.required && (
                  <Box component="span" sx={{ color: status.error.solid, ml: 0.4 }}>
                    *
                  </Box>
                )}
              </Txt>
              {item?.extracted ? (
                <Pill label="Extracted" c={steel.main} bg={steel.tint} size="xs" />
              ) : present ? (
                <Pill label="Not yet extracted" c={amber.chipText} bg={amber.light} size="xs" />
              ) : null}
            </Flx>
            <Muted sx={{ display: "block", fontSize: 11.5, mt: 0.25 }}>
              {[item?.owner ? `Delivered by ${item.owner}` : null, item?.required ? "required" : "optional"]
                .filter(Boolean)
                .join(" · ")}
            </Muted>

            {docs.length > 0 && (
              <Flx column g={0.35} sx={{ mt: 0.75 }}>
                {docs.map((d, i) => {
                  const sm = docStatusMeta(d?.status);
                  const full = resolveDoc(d?.id, d);
                  const clickable = !!full;
                  return (
                    <Flx
                      ac
                      g={0.75}
                      key={d?.id || i}
                      onClick={clickable ? () => onPreview(full) : undefined}
                      sx={{
                        minWidth: 0,
                        cursor: clickable ? "pointer" : "default",
                        "&:hover .doc-name": clickable ? { color: steel.main, textDecoration: "underline" } : {},
                      }}
                    >
                      <DescriptionOutlined sx={{ fontSize: 14, color: slate[400], flexShrink: 0 }} />
                      <Txt
                        className="doc-name"
                        sx={{
                          fontSize: 12.5,
                          color: slate[800],
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                        }}
                      >
                        {d?.fileName || d?.docType}
                      </Txt>
                      <Pill label={d?.docType || "—"} c={slate[500]} bg={slate[100]} size="xs" />
                      {d?.status && <Pill label={d.status} c={sm.c} bg={sm.bg} size="xs" />}
                      {d?.uploadedAt && (
                        <Muted sx={{ fontSize: 11, flexShrink: 0 }}>{formatDateTime(d.uploadedAt, "date")}</Muted>
                      )}
                    </Flx>
                  );
                })}
              </Flx>
            )}
          </Box>
        </Flx>

        {/* Upload: doc-type picker (defaults to the first docType) + button. */}
        <Flx ac g={1} sx={{ flexShrink: 0, alignItems: "flex-end" }}>
          {item?.docTypes?.length > 1 && (
            <TextField
              select
              size="small"
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              sx={{ minWidth: 200, ".MuiInputBase-root": { fontSize: 12.5 } }}
            >
              {item.docTypes.map((t) => (
                <MenuItem key={t} value={t} sx={{ fontSize: 12.5 }}>
                  {t}
                </MenuItem>
              ))}
            </TextField>
          )}
          <input ref={inputRef} type="file" multiple hidden onChange={onPick} />
          <Tooltip title={`Upload as "${docType}"`} arrow>
            <span>
              <Button
                size="small"
                variant={present ? "outlined" : "contained"}
                color={present ? "gray" : "primary"}
                startIcon={uploading ? <CircularProgress size={12} /> : <UploadFileOutlined />}
                onClick={() => inputRef.current?.click()}
                disabled={uploading || !docType}
                sx={{ textTransform: "none", borderRadius: "8px", whiteSpace: "nowrap" }}
              >
                Upload
              </Button>
            </span>
          </Tooltip>
        </Flx>
      </Flx>
    </Box>
  );
};

/**
 * @param dealId       the loan
 * @param pipeline     "preclose" | "acquisition" (drives the requirement set)
 * @param loanDocuments the loan's full `loanDocuments[]` — used to resolve a
 *                     row's summary doc to the full object the previewer needs
 * @param onChanged    called after an upload / compare so the parent can
 *                     refetch the loan + readiness
 * @param refreshKey   bump to refetch the section (e.g. after the loan reloads)
 */
const ClosingDocumentsSection = ({ dealId, pipeline, loanDocuments, onChanged, refreshKey }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const groupsAndTypes = useSelector((state) => state.fetchedSelections?.fileDocGroupAndTypes);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [indexing, setIndexing] = useState(false);

  const load = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      setData(await closingReadinessApi.getClosingDocs(dealId, { pipeline }));
    } catch (err) {
      console.error("closing-docs fetch error:", err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId, pipeline]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // docType → docGroup from the cached doc taxonomy ({ [group]: [types] }).
  const docGroupFor = useCallback(
    (docType) => {
      if (!groupsAndTypes || typeof groupsAndTypes !== "object") return undefined;
      const hit = Object.entries(groupsAndTypes).find(
        ([, types]) => Array.isArray(types) && types.includes(docType),
      );
      return hit ? hit[0] : undefined;
    },
    [groupsAndTypes],
  );

  const docsById = useMemo(() => {
    const m = new Map();
    (Array.isArray(loanDocuments) ? loanDocuments : []).forEach((d) => {
      if (d?._id) m.set(String(d._id), d);
    });
    return m;
  }, [loanDocuments]);
  const resolveDoc = useCallback((id) => (id ? docsById.get(String(id)) || null : null), [docsById]);
  const onPreview = useCallback((doc) => dispatch(filePreviewSetData(doc)), [dispatch]);

  const onUploaded = useCallback(() => {
    load();
    if (typeof onChanged === "function") onChanged();
  }, [load, onChanged]);

  const compare = async () => {
    if (!dealId) return;
    setComparing(true);
    try {
      const res = await closingReadinessApi.compareClosingDocs(dealId, { pipeline });
      if (res?.closingDocuments) setData((d) => ({ ...(d || {}), closingDocuments: res.closingDocuments }));
      const t = res?.closingDocuments?.termsTieOut;
      enqueueSnackbar(
        !res?.ran || t?.agrees == null
          ? "Nothing to compare yet — upload and extract the Note / HUD first."
          : t.agrees
            ? "Closing documents tie to the loan terms."
            : `${res.findingsStaged ?? t.findingCount ?? 0} divergence${(res.findingsStaged ?? t.findingCount) === 1 ? "" : "s"} staged as findings.`,
        { variant: !res?.ran || t?.agrees == null ? "info" : t.agrees ? "success" : "warning" },
      );
      if (typeof onChanged === "function") onChanged();
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Compare failed.", { variant: "error" });
    } finally {
      setComparing(false);
    }
  };

  // Monolithic package PDF → carve into children so the package row counts.
  const indexPackage = async () => {
    if (!dealId) return;
    setIndexing(true);
    try {
      const res = await losAiReviewsApi.runUnexecutedPackageIndex(dealId, {});
      enqueueSnackbar(
        res?.status === "skipped"
          ? res?.message || "Already indexed."
          : "Index Unexecuted Package queued — the carved Note / HUD will appear when it finishes.",
        { variant: res?.status === "skipped" ? "info" : "success" },
      );
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.detail || "Could not queue the indexer.", { variant: "error" });
    } finally {
      setIndexing(false);
    }
  };

  const cd = data?.closingDocuments;
  const items = Array.isArray(cd?.items) ? cd.items : [];
  const trade = isTrade({ pipeline: data?.pipeline || pipeline });
  const missing = Array.isArray(cd?.missingRequired) ? cd.missingRequired : [];

  return (
    <Box>
      <BlockTitle
        right={
          <Flx ac g={0.75}>
            {loading && <CircularProgress size={12} sx={{ color: slate[400] }} />}
            {cd && (
              <Pill
                label={cd.allRequiredPresent ? "All required present" : `${missing.length} required missing`}
                c={cd.allRequiredPresent ? status.success.fg : status.error.fg}
                bg={cd.allRequiredPresent ? status.success.bg : status.error.bg}
                size="xs"
              />
            )}
            {!trade && (
              <Tooltip title="For a monolithic package PDF: carve it into Unexecuted Note / Settlement Statement / … so the package row counts and extraction runs." arrow>
                <span>
                  <Button
                    size="small"
                    variant="text"
                    onClick={indexPackage}
                    disabled={indexing}
                    sx={{ textTransform: "none", fontSize: 12 }}
                  >
                    {indexing ? "Queuing…" : "Index unexecuted package"}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Flx>
        }
      >
        Required for funding
      </BlockTitle>
      <Muted sx={{ display: "block", mb: 1.25 }}>
        {trade
          ? "The seller's executed package and final HUD gate funding. Files land on the loan like any other document."
          : "The unexecuted loan package, preliminary HUD and title wire instructions gate funding. Files land on the loan like any other document; extraction runs on upload."}
      </Muted>

      {!data && !loading ? (
        <Muted>Closing documents unavailable for this loan.</Muted>
      ) : (
        <Flx column g={1}>
          {items.map((item) => (
            <RequirementRow
              key={item.key}
              item={item}
              dealId={dealId}
              docGroupFor={docGroupFor}
              onUploaded={onUploaded}
              resolveDoc={resolveDoc}
              onPreview={onPreview}
            />
          ))}
          {cd && (
            <Box sx={{ mt: 0.5 }}>
              <TieOutBanner tieOut={cd.termsTieOut} onCompare={compare} comparing={comparing} />
            </Box>
          )}
        </Flx>
      )}
    </Box>
  );
};

export default ClosingDocumentsSection;
